import { ChangeDetectorRef, ContentChild, Directive, ElementRef, EventEmitter, Input, NgZone, Output, SimpleChanges, TemplateRef, ViewContainerRef } from "@angular/core";
import { fileUploadEvent } from "./ad-upload.directive";
import { AdUploadService } from "./ad-upload.service";
import _ from 'lodash';

@Directive({
  selector: '[ad-upload-area]',
  host: {
    class: 'a-upload-area',
  }
})
export class AdUploadAreaDirective {
  @Input() name: string;

  @Input() url: string;

  @Input() method: string = 'post';

  @Input() disabled: boolean;

  @Input() auto: boolean;

  @Input() applyHoverStyle: boolean;

  @Input() showSpin: boolean | 'spin';

  @Input() hoverContent: TemplateRef<any>;

  @Input() hoverContext: any = {};

  @Output() onSelect: EventEmitter<FileList> = new EventEmitter<FileList>();

  @Output() onUploaded: EventEmitter<fileUploadEvent> = new EventEmitter<fileUploadEvent>();

  _lastFiles: FileList;

  filesInDragging: FileList;

  _container: HTMLElement;

  _hoverElement: HTMLElement;
  isHoverAttached: boolean = false;

  detachView: () => void = null;

  refreshCount: number = 0;
  refreshInterval: any;


  constructor(public el: ElementRef,
    public uploadService: AdUploadService,
    public zone: NgZone,
    private viewContainer: ViewContainerRef,
    private cd: ChangeDetectorRef,
  ) {
    this._container = el.nativeElement;
    this.bindingListener();
  }

  ngOnInit() {
  }




  ngAfterContentInit() {
    if (this.hoverContent == null) {
      this.createHoverElement();
    } else {
      let elementRef = this.viewContainer.createEmbeddedView(this.hoverContent, this.hoverContext);
      this._hoverElement = elementRef.rootNodes[0];
      this._hoverElement.parentElement.removeChild(this._hoverElement);
      this.detachView = () => elementRef.destroy();
      this.cd.detectChanges();
    }
  }

  ngOnDestroy() {
    this.unbindingListener();
    this.detachView?.();
  }

  createHoverElement() {
    this._hoverElement = document.createElement('div');
    this._hoverElement.className = 'flex-center upload-mask';
    const uploadborder = document.createElement('div');
    uploadborder.className = 'upload-border flex-center';
    const plusIcon = document.createElement('i');
    plusIcon.className = 'fa fa-plus';
    this._hoverElement.appendChild(uploadborder);
    uploadborder.appendChild(plusIcon);
  }

  attachHoverElement() {
    if (!this.isHoverAttached) {
      this._container.className = `${this._container.className} a-fileon`;
      this._container.appendChild(this._hoverElement);
      this.isHoverAttached = true;
    }
  }

  detachHoverElement() {
    if (this.isHoverAttached) {
      this._container.className = this._container.className.replace(/\sa-fileon$/i, '');
      this._container.removeChild(this._hoverElement);
      this.isHoverAttached = false;
      if (this.refreshInterval != null) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
        this.refreshCount = 0;
      }
    }
  }

  onDrop = (event: DragEvent) => {
    if (!this.disabled) {
      event.preventDefault();
      event.stopPropagation();
      if (event.dataTransfer.files?.length > 0) {
        let files: FileList = event.dataTransfer.files;
        this._lastFiles = files;
        this.onSelect.emit(files);
        if (this.auto) {
          this.uploadService.uploadFiles(
            this.uploadService.transformToFormData(files, this.name),
            this.url,
            this.method,
            this.showSpin
          ).subscribe((param: any) => {
            this.onUploaded.emit({
              files,
              param
            });
          }, (err) => {
            this.onUploaded.emit({
              files,
              err
            });
          });
        }
      }
      this.detachHoverElement();
    }
  }

  onDragOver = (event: DragEvent) => {
    if (!this.disabled) {
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "move";
      if (this.applyHoverStyle) {
        this.refresh();
      }
    }
  }

  refresh() {
    if (this.refreshInterval == null) {
      this.refreshCount++;
      let count = this.refreshCount;
      this.refreshInterval = setInterval(() => {
        if (this.isHoverAttached) {
          if (count === this.refreshCount) {
            this.detachHoverElement();
          } else {
            count = this.refreshCount;
          }
        } else {
          this.attachHoverElement();
          count = this.refreshCount;
        }
      }, 100);
    } else {
      this.refreshCount++;
    }
  }

  onDragLeave = _.debounce((event: DragEvent) => {
    if (this.disabled) return;
    if (this.applyHoverStyle && this.isHoverAttached && this.isOkForLeave(event.target as HTMLElement, event.relatedTarget as HTMLElement)) {
      console.log(`drag leave: ${(event.target as any).className}`);
      this.detachHoverElement();
    }
  }, 0);

  onDragEnter = _.debounce((event: DragEvent) => {
    if (this.disabled) return;
    if (this.applyHoverStyle && this.isOkForEnter(event.target as HTMLElement, event.relatedTarget as HTMLElement)) {
      console.log(`drag enter: ${(event.target as any).className}`);
      this.attachHoverElement();
    }
  }, 0);

  bindingListener() {
    if (this._container) {
      this._container.addEventListener('drop', this.onDrop, true);
      this.zone.runOutsideAngular(() => {
        this._container.addEventListener('dragover', this.onDragOver, true);
        this._container.addEventListener('dragleave', this.onDragLeave, true);
        this._container.addEventListener('dragenter', this.onDragEnter, true);
      });
    }
  }

  unbindingListener() {
    if (this._container) {
      this._container.removeEventListener('drop', this.onDrop, true);
      this._container.removeEventListener('dragover', this.onDragOver, true);
      this._container.removeEventListener('dragleave', this.onDragLeave, true);
      this._container.removeEventListener('dragenter', this.onDragEnter, true);
    }
  }

  isInContainer(element: HTMLElement) {
    const eleRect = element.getBoundingClientRect();
    const containRect = this._container.getBoundingClientRect();
    const [left, top] = [eleRect.left - containRect.left, eleRect.top - containRect.top];
    return left >= 0 && top >= 0;
  }

  isOkForEnter(inElement: HTMLElement, outElement: HTMLElement) {
    return this.isInContainer(inElement) && !this.isInContainer(outElement);
  }

  isOkForLeave(outElement: HTMLElement, inElement: HTMLElement) {
    return this.isInContainer(outElement) && !this.isInContainer(inElement);
  }
}
